import React from "react";
import Seo from "../components/Seo";
import { LegalLayout, LegalSection } from "../components/LegalLayout";

// Tabella condivisa per le tre categorie di cookie. Sharp edges, mandrogni
// border, condensed eyebrows — stile OnSports editorial.
const CookieTable = ({ rows }) => (
  <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
    <table className="w-full text-left text-[14px] border-collapse">
      <thead>
        <tr className="bg-dodgers text-galactico">
          <th className="border border-dodgers px-3 py-3 font-condensed uppercase tracking-widest text-[11px]">
            Nome cookie
          </th>
          <th className="border border-dodgers px-3 py-3 font-condensed uppercase tracking-widest text-[11px]">
            Provider
          </th>
          <th className="border border-dodgers px-3 py-3 font-condensed uppercase tracking-widest text-[11px]">
            Finalità
          </th>
          <th className="border border-dodgers px-3 py-3 font-condensed uppercase tracking-widest text-[11px]">
            Durata
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td className="border border-mandrogni/40 px-3 py-2.5 align-top">
              <code className="text-[13px]">{r.name}</code>
            </td>
            <td className="border border-mandrogni/40 px-3 py-2.5 align-top">{r.provider}</td>
            <td className="border border-mandrogni/40 px-3 py-2.5 align-top">{r.purpose}</td>
            <td className="border border-mandrogni/40 px-3 py-2.5 align-top whitespace-nowrap">
              {r.duration}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CookiePolicy = () => {
  return (
    <>
      <Seo
        title="Cookie Policy GDPR — Cookie utilizzati | OnSports"
        description="Informativa sui cookie del sito OnSports: cookie tecnici, analitici (GA4, Clarity), marketing (LeadInfo). Gestione consensi conforme GDPR e Garante italiano."
        path="/cookie-policy"
      />
      <LegalLayout title="Informativa sull'utilizzo dei cookie" lastUpdate="25 maggio 2026 · Versione 2.0">
        <LegalSection title="Cosa sono i cookie">
          <p>
            I cookie sono piccoli file di testo che i siti web visitati inviano
            al dispositivo dell'utente, dove vengono memorizzati per essere
            ritrasmessi al sito alla visita successiva. La presente Cookie
            Policy descrive nel dettaglio i cookie utilizzati dal sito{" "}
            <strong>www.weareonsports.com</strong>.
          </p>
        </LegalSection>

        <LegalSection title="Tipologie di cookie utilizzati">
          <h3 className="font-bold uppercase tracking-wide text-[16px] mt-2 mb-3">
            Cookie tecnici (necessari)
          </h3>
          <p>
            Indispensabili al corretto funzionamento del sito (memorizzazione
            preferenze, gestione sessione, gestione consenso cookie). Non
            richiedono consenso dell'utente.
          </p>
          <CookieTable
            rows={[
              {
                name: "onsports-cookie-consent",
                provider: "OnSports",
                purpose: "Memorizza le preferenze cookie dell'utente",
                duration: "12 mesi",
              },
            ]}
          />

          <h3 className="font-bold uppercase tracking-wide text-[16px] mt-10 mb-3">
            Cookie analitici (statistica)
          </h3>
          <p>
            Utilizzati per raccogliere informazioni statistiche aggregate
            sull'utilizzo del sito.{" "}
            <strong>Richiedono il consenso esplicito dell'utente</strong>{" "}
            tramite il banner di consenso.
          </p>
          <CookieTable
            rows={[
              {
                name: "_ga",
                provider: "Google Analytics",
                purpose: "Identificazione unica utente",
                duration: "2 anni",
              },
              {
                name: "_ga_T0P20ZXJ8B",
                provider: "Google Analytics",
                purpose: "Stato della sessione",
                duration: "2 anni",
              },
              {
                name: "_clck",
                provider: "Microsoft Clarity",
                purpose: "Identificazione utente per session recording",
                duration: "1 anno",
              },
              {
                name: "_clsk",
                provider: "Microsoft Clarity",
                purpose: "Aggregazione pageview per sessione",
                duration: "1 giorno",
              },
              {
                name: "CLID",
                provider: "Microsoft Clarity",
                purpose: "Identificazione cross-domain Clarity",
                duration: "1 anno",
              },
              {
                name: "MUID",
                provider: "Microsoft",
                purpose: "Identificazione browser cross-domain Microsoft",
                duration: "13 mesi",
              },
            ]}
          />

          <h3 className="font-bold uppercase tracking-wide text-[16px] mt-10 mb-3">
            Cookie di marketing
          </h3>
          <p>
            Utilizzati per identificare le aziende che visitano il sito a fini
            commerciali B2B.{" "}
            <strong>Richiedono il consenso esplicito dell'utente</strong>{" "}
            tramite il banner di consenso.
          </p>
          <CookieTable
            rows={[
              {
                name: "_li_id",
                provider: "LeadInfo",
                purpose: "Identificazione azienda visitante (IP business)",
                duration: "6 mesi",
              },
              {
                name: "_li_ses",
                provider: "LeadInfo",
                purpose: "Identificazione sessione LeadInfo",
                duration: "Sessione",
              },
            ]}
          />
        </LegalSection>

        <LegalSection title="Come gestire le preferenze">
          <p>Al primo accesso al sito, viene mostrato un banner che consente di:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Accettare tutti i cookie</strong>: consente sia i cookie
              analitici sia quelli di marketing.
            </li>
            <li>
              <strong>Rifiutare tutti i cookie</strong>: blocca i cookie
              analitici e di marketing. Restano attivi solo i cookie tecnici
              necessari.
            </li>
            <li>
              <strong>Personalizzare le preferenze</strong>: scegliere
              singolarmente quali categorie di cookie consentire.
            </li>
          </ul>
          <p>
            È possibile modificare o revocare il consenso in qualsiasi momento
            cliccando su <strong>"Gestisci cookie"</strong> nel footer di ogni
            pagina del sito.
          </p>
        </LegalSection>

        <LegalSection title="Disabilitare i cookie dal browser">
          <p>
            È inoltre possibile disabilitare i cookie direttamente dalle
            impostazioni del proprio browser. Di seguito i link alle guide
            ufficiali:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <a
                className="link-os"
                href="https://support.google.com/chrome/answer/95647"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Chrome
              </a>
            </li>
            <li>
              <a
                className="link-os"
                href="https://support.mozilla.org/it/kb/Gestione%20dei%20cookie"
                target="_blank"
                rel="noopener noreferrer"
              >
                Mozilla Firefox
              </a>
            </li>
            <li>
              <a
                className="link-os"
                href="https://support.apple.com/it-it/guide/safari/sfri11471/mac"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apple Safari
              </a>
            </li>
            <li>
              <a
                className="link-os"
                href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                target="_blank"
                rel="noopener noreferrer"
              >
                Microsoft Edge
              </a>
            </li>
          </ul>
        </LegalSection>

        <LegalSection title="Titolare del trattamento">
          <p>
            <strong>OnSports S.r.l.</strong>
            <br />
            Via Jacopo Dal Verme 7, 20159 Milano (MI)
            <br />
            P.IVA 11367080964
            <br />
            Email:{" "}
            <a className="link-os" href="mailto:privacy@weareonsports.com">
              privacy@weareonsports.com
            </a>
          </p>
          <p>
            Per maggiori informazioni sul trattamento dei dati personali,
            consulta la nostra{" "}
            <a className="link-os" href="/privacy-policy">
              Privacy Policy
            </a>
            .
          </p>
        </LegalSection>
      </LegalLayout>
    </>
  );
};

export default CookiePolicy;
